<template>
    <div class="tp-config">

        <!-- Barre d'outils -->
        <div class="d-flex justify-content-between align-items-center mb-2">
            <span class="small text-muted">{{ params_list.length }} paramètre(s)</span>
            <b-button size="sm" variant="outline-primary" @click="open_create">
                <i class="fas fa-plus mr-1"></i>Nouveau paramètre
            </b-button>
        </div>

        <!-- Liste vide -->
        <div v-if="params_list.length === 0" class="text-muted small py-2">
            Aucun paramètre défini. Cliquez sur "Nouveau paramètre" pour commencer.
        </div>

        <!-- Liste draggable -->
        <draggable v-else v-model="params_list" handle=".tp-drag" @end="emit_change" :animation="150">
            <div
                v-for="(param, idx) in params_list"
                :key="get_key(param, idx)"
                class="d-flex align-items-center border rounded px-2 py-1 mb-1"
                style="background:#fff"
            >
                <i class="fas fa-grip-vertical tp-drag mr-2 text-muted" style="cursor:grab; flex-shrink:0"></i>
                <span class="text-muted small mr-2" style="min-width:18px; flex-shrink:0">{{ idx + 1 }}</span>

                <div class="flex-grow-1" style="min-width:0; overflow:hidden">
                    <strong>{{ param.libelle || param.name }}</strong>
                    <code class="ml-1 small text-muted">{{ param.name }}</code>
                    <b-badge v-if="!is_v2(param)" variant="secondary" pill class="ml-1" style="font-size:.6rem" v-b-tooltip.hover title="Paramètre au format legacy (une seule étape ciblée)">legacy</b-badge>
                    <b-badge
                        v-if="has_modified_value(param)"
                        variant="warning" pill class="ml-1" style="font-size:.6rem"
                        v-b-tooltip.hover :title="'Valeur test courante : ' + param.value + ' (défaut : ' + param.default_value + ')'"
                    >modifié</b-badge>
                    <span class="text-muted small ml-2">{{ type_label(param) }}</span>
                    <span v-if="is_v2(param)" class="text-muted small ml-2">
                        <i class="fas fa-crosshairs"></i> {{ (param.targets || []).length }} étape(s)
                    </span>
                    <span v-if="param.required" class="text-danger small ml-1">*</span>
                </div>

                <div class="d-flex align-items-center ml-1" style="flex-shrink:0; gap:2px">
                    <b-button
                        v-if="has_modified_value(param)"
                        size="sm" variant="link" class="p-1 text-warning"
                        v-b-tooltip.hover title="Réinitialiser la valeur courante"
                        @click.stop="reset_value(idx)"
                    ><i class="fas fa-undo fa-sm"></i></b-button>
                    <b-button size="sm" variant="link" class="p-1" @click.stop="open_edit(idx)">
                        <i class="fas fa-edit fa-sm"></i>
                    </b-button>
                    <b-button size="sm" variant="link" class="p-1 text-danger" @click.stop="confirm_delete(idx)">
                        <i class="fas fa-trash fa-sm"></i>
                    </b-button>
                </div>
            </div>
        </draggable>

        <b-modal
            v-model="show_modal"
            :title="editing_idx === -1 ? 'Nouveau paramètre' : 'Modifier le paramètre'"
            size="lg"
            :ok-title="editing_idx === -1 ? 'Créer' : 'Enregistrer'"
            cancel-title="Annuler"
            @ok="on_modal_ok"
            @hidden="reset_form"
            no-close-on-backdrop
        >
            <b-form @submit.prevent>

                <!-- ── Définition pleine largeur ── -->
                <p class="text-uppercase text-muted small font-weight-bold mb-2">Définition</p>

                <b-row>
                    <b-col cols="12" md="4">
                        <b-form-group
                            label="Nom (identifiant) *"
                            :state="name_error ? false : null"
                            :invalid-feedback="name_error"
                        >
                            <b-input
                                v-model="form.name"
                                :state="name_error ? false : null"
                                @input="validate_name"
                                placeholder="ex: start_date"
                                trim
                            ></b-input>
                            <b-form-text>Commence par une lettre ou <code>_</code>, puis lettres, chiffres et <code>_</code>.</b-form-text>
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="4">
                        <b-form-group label="Libellé affiché">
                            <b-input v-model="form.libelle" placeholder="ex: Date de début"></b-input>
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="4">
                        <b-form-group label="Description (info-bulle)">
                            <b-input v-model="form.description" placeholder="Explication pour l'utilisateur..."></b-input>
                        </b-form-group>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col cols="12" md="3">
                        <b-form-group label="Type">
                            <b-form-select v-model="form.type_key" :options="type_options" @change="on_type_change"></b-form-select>
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="3" v-if="form.type_key === 'texte'">
                        <b-form-group label="Type HTML">
                            <b-form-select v-model="form.html_type" :options="html_type_options"></b-form-select>
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="3" v-if="form.type_key === 'textarea'">
                        <b-form-group label="Nombre de lignes">
                            <b-input v-model.number="form.textarea_rows" type="number" min="1"></b-input>
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="2" v-if="form.type_key === 'liste'">
                        <b-form-group label=" ">
                            <b-form-checkbox v-model="form.list_multiple" class="mt-2">Choix multiples</b-form-checkbox>
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="3" v-if="form.type_key === 'database'">
                        <b-form-group label="Table par défaut">
                            <b-input v-model="form.table" placeholder="nom_table"></b-input>
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="3" v-if="['pdf_zones', 'docx'].includes(form.type_key)">
                        <b-form-group label="Colonne type document">
                            <b-input v-model="form.type_doc" placeholder="colonne_type_doc"></b-input>
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="2" class="d-flex align-items-center">
                        <b-form-group label=" ">
                            <b-form-checkbox v-model="form.required" class="mt-2">Obligatoire</b-form-checkbox>
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row class="mt-2">
                    <b-col cols="12">
                        <b-form-group label="Valeur par défaut">
                            <b-form-checkbox
                                v-if="form.type_key === 'booleen'"
                                v-model="form.default_value"
                                :value="true"
                                :unchecked-value="false"
                            >Activé par défaut</b-form-checkbox>
                            <b-input
                                v-else-if="form.type_key === 'nombre'"
                                v-model="form.default_value"
                                type="number"
                            ></b-input>
                            <b-input
                                v-else-if="form.type_key === 'date'"
                                v-model="form.default_value"
                                type="date"
                            ></b-input>
                            <b-textarea v-else v-model="form.default_value" rows="2"></b-textarea>
                        </b-form-group>
                    </b-col>
                </b-row>

                <!-- Options avancées Texte / Nombre / Date (datalist + db) -->
                <div v-if="['texte', 'nombre', 'date'].includes(form.type_key)" class="mb-2">
                    <a href="#" class="small" @click.prevent="form.show_texte_advanced = !form.show_texte_advanced">
                        <i :class="form.show_texte_advanced ? 'fas fa-chevron-down' : 'fas fa-chevron-right'" class="mr-1"></i>Options avancées
                    </a>
                    <b-collapse v-model="form.show_texte_advanced">
                        <b-row class="mt-2 p-2 border rounded" style="background:#f8f9fa; margin:0">
                            <b-col cols="12" md="6">
                                <b-form-group label="Autocomplétion (datalist)" description="Une option par ligne">
                                    <b-textarea v-model="form.datalist_raw" rows="3" :placeholder="'Lundi\nMardi\nMercredi'"></b-textarea>
                                </b-form-group>
                            </b-col>
                            <b-col cols="12" md="6">
                                <label class="col-form-label pt-0 pb-1">Autocomplétion depuis la base de données</label>
                                <b-row>
                                    <b-col cols="6"><b-form-group label="Clé" class="mb-1"><b-input v-model="form.db_value" size="sm" placeholder="id"></b-input></b-form-group></b-col>
                                    <b-col cols="6"><b-form-group label="Libellé" class="mb-1"><b-input v-model="form.db_libelle" size="sm" placeholder="nom"></b-input></b-form-group></b-col>
                                    <b-col cols="12"><b-form-group label="Table" class="mb-1"><b-input v-model="form.db_table" size="sm" placeholder="ma_table"></b-input></b-form-group></b-col>
                                    <b-col cols="12"><b-form-group label="Where" class="mb-1"><b-input v-model="form.db_where" size="sm" placeholder="actif = 1"></b-input></b-form-group></b-col>
                                    <b-col cols="12"><b-form-group label="Fin (ORDER BY…)" class="mb-1"><b-input v-model="form.db_end" size="sm" placeholder="ORDER BY nom ASC"></b-input></b-form-group></b-col>
                                </b-row>
                            </b-col>
                        </b-row>
                    </b-collapse>
                </div>

                <!-- Options (liste, checkbox, radio…) -->
                <b-form-group v-if="type_has_options" label="Options (une par ligne)">
                    <b-textarea
                        v-model="form.list_options_raw"
                        rows="4"
                        :placeholder="'Option A\nOption B\nvaleur:Libellé personnalisé'"
                    ></b-textarea>
                    <b-form-text v-if="form.type_key === 'autocomplete'">Une option par ligne (texte affiché uniquement)</b-form-text>
                    <b-form-text v-else>Format : <code>valeur</code> ou <code>valeur:Libellé</code></b-form-text>
                </b-form-group>

                <hr class="my-3" />

                <!-- ── Étapes ciblées pleine largeur ── -->
                <p class="text-uppercase text-muted small font-weight-bold mb-1">Étapes ciblées</p>
                <p class="text-muted small mb-2">
                    Ce paramètre remplacera les champs sélectionnés à l'exécution.
                    Laisser vide pour un paramètre accessible uniquement via les scripts.
                </p>

                <b-row>
                    <b-col
                        v-for="(target, tidx) in form.targets"
                        :key="tidx"
                        cols="12" md="6" lg="4"
                        class="mb-2"
                    >
                        <div class="border rounded p-2 h-100">
                            <div class="d-flex align-items-center" style="gap:6px">
                                <b-form-select
                                    v-model="target.step_uid"
                                    :options="step_options"
                                    size="sm"
                                    class="flex-grow-1"
                                    @change="on_target_step_change(tidx)"
                                ></b-form-select>
                                <b-button
                                    size="sm" variant="link" class="p-0 text-danger"
                                    style="flex-shrink:0"
                                    @click="remove_target(tidx)"
                                ><i class="fas fa-times"></i></b-button>
                            </div>

                            <div v-if="target.step_uid" class="mt-2">
                                <template v-if="get_step_action(target.step_uid) === 'projet'">
                                    <b-form-group label="Nom du paramètre (projet fils)" class="mb-0">
                                        <b-form-select
                                            v-if="get_child_param_options(target.step_uid).length > 1"
                                            v-model="target.param_name"
                                            :options="get_child_param_options(target.step_uid)"
                                            size="sm"
                                            @change="val => on_child_param_select(target.step_uid, val)"
                                        ></b-form-select>
                                        <b-input v-else v-model="target.param_name" size="sm" placeholder="nom_du_parametre" trim></b-input>
                                    </b-form-group>
                                </template>
                                <template v-else-if="is_script_action(target.step_uid)">
                                    <p class="text-muted small mb-0">
                                        <i class="fas fa-info-circle mr-1"></i>
                                        Les scripts accèdent à tous les paramètres via <code>treatment_params</code>.
                                    </p>
                                </template>
                                <template v-else>
                                    <b-form-group label="Champ à remplacer" class="mb-0">
                                        <b-form-select
                                            v-if="get_known_field_options(target.step_uid).length > 1"
                                            v-model="target._field_key"
                                            :options="[{ value: '', text: '— Sélectionner —' }, ...get_known_field_options(target.step_uid)]"
                                            size="sm"
                                            class="mb-1"
                                            @change="val => on_field_select(tidx, val)"
                                        ></b-form-select>
                                        <b-input
                                            v-if="!target._field_key || target._field_key === '__custom__' || get_known_field_options(target.step_uid).length <= 1"
                                            v-model="target.jsonpath"
                                            size="sm"
                                            placeholder="$.champ ou $.objet.sous_champ"
                                            trim
                                        ></b-input>
                                    </b-form-group>
                                </template>
                            </div>
                        </div>
                    </b-col>
                </b-row>

                <b-button size="sm" variant="outline-secondary" class="mt-1" @click="add_target">
                    <i class="fas fa-plus mr-1"></i>Ajouter une étape
                </b-button>

            </b-form>
        </b-modal>

    </div>
</template>

<script>
    import draggable from "vuedraggable";
    import { getKnownFields, isScriptAction } from "../config/treatment_params_fields.js";

    var clone = require("clone");

    export default {
        name: "treatment_params_config",
        components: { draggable },
        props: {
            /** Liste complète des treatment_params (v-model) */
            value: {
                type: Array,
                default: () => [],
            },
            /** Étapes du scénario : [{ uid, description, action, position }] */
            steps: {
                type: Array,
                default: () => [],
            },
        },
        data() {
            return {
                params_list: [],
                show_modal: false,
                editing_idx: -1,
                form: this.empty_form(),
                name_error: "",
            };
        },
        watch: {
            value: {
                immediate: true,
                handler(val) {
                    var incoming = val || [];
                    // Si même éléments (par name) déjà présents, ne pas réinitialiser l'ordre local
                    if (this.params_list.length === incoming.length) {
                        var cur_names = this.params_list.map(p => p.name).join(",");
                        var inc_names_sorted = [...incoming].map(p => p.name).sort().join(",");
                        var cur_names_sorted = this.params_list.map(p => p.name).sort().join(",");
                        if (cur_names_sorted === inc_names_sorted) {
                            // Mettre à jour les propriétés de chaque param sans changer l'ordre
                            for (var i = 0; i < this.params_list.length; i++) {
                                var match = incoming.find(p => p.name === this.params_list[i].name);
                                if (match) {
                                    var keys = Object.keys(match);
                                    for (var k = 0; k < keys.length; k++) {
                                        if (JSON.stringify(this.params_list[i][keys[k]]) !== JSON.stringify(match[keys[k]])) {
                                            this.$set(this.params_list[i], keys[k], clone(match[keys[k]]));
                                        }
                                    }
                                }
                            }
                            return;
                        }
                    }
                    this.params_list = clone(incoming);
                },
            },
        },
        computed: {
            type_options() {
                var pinned = [
                    { value: null,                text: "— Choisir un type —" },
                    { value: "texte",              text: "Texte" },
                    { value: "nombre",             text: "Nombre" },
                    { value: "booleen",            text: "Booléen (vrai/faux)" },
                    { value: "textarea",           text: "Zone de texte" },
                ];
                var rest = [
                    { value: "autocomplete",       text: "Autocomplétion" },
                    { value: "autocomplete_list",  text: "Autocomplétion (liste)" },
                    { value: "database",           text: "Base de données" },
                    { value: "database_multi",     text: "Base de données multitables" },
                    { value: "date",               text: "Date" },
                    { value: "documents",          text: "Documents" },
                    { value: "docx",               text: "Docx" },
                    { value: "draggable_list",     text: "Drag&drop - liste" },
                    { value: "json_schema_editor", text: "Éditeur JSON Schema" },
                    { value: "file_names",         text: "Fichiers - noms" },
                    { value: "html_zones",         text: "Zones HTML" },
                    { value: "hidden",             text: "Invisible" },
                    { value: "liste",              text: "Liste déroulante" },
                    { value: "mapping",            text: "Mapping" },
                    { value: "object",             text: "Objet" },
                    { value: "pdf_zones",          text: "Zones PDF" },
                    { value: "radio",              text: "Boutons radio" },
                    { value: "csv",                text: "Tableau (CSV)" },
                    { value: "checkbox",           text: "Cases à cocher" },
                    { value: "textarea_plus",      text: "Zone de texte avancée" },
                    { value: "null",               text: "Vide" },
                ].sort(function(a, b) { return a.text.localeCompare(b.text, 'fr', { sensitivity: 'base' }); });
                return pinned.concat(rest);
            },
            html_type_options() {
                return [
                    { value: "",         text: "— Aucun (texte libre) —" },
                    { value: "email",    text: "Email" },
                    { value: "tel",      text: "Téléphone" },
                    { value: "url",      text: "URL" },
                    { value: "color",    text: "Couleur" },
                    { value: "password", text: "Mot de passe" },
                    { value: "month",    text: "Mois" },
                    { value: "time",     text: "Heure" },
                    { value: "week",     text: "Semaine" },
                    { value: "range",    text: "Curseur (range)" },
                ];
            },
            type_has_options() {
                return ["liste", "checkbox", "radio", "autocomplete", "autocomplete_list", "draggable_list"].includes(this.form.type_key);
            },
            step_options() {
                var opts = [{ value: null, text: "— Sélectionner une étape —" }];
                for (var s of this.steps) {
                    var label = "Étape " + s.position;
                    if (s.description) label += " — " + s.description;
                    opts.push({ value: s.uid, text: label });
                }
                return opts;
            },
            parsed_list_options() {
                if (!this.form.list_options_raw) return [];
                return this.form.list_options_raw
                    .split("\n")
                    .map(l => l.trim())
                    .filter(l => l)
                    .map(l => {
                        var sep = l.indexOf(":");
                        if (sep > 0) {
                            return { value: l.slice(0, sep).trim(), text: l.slice(sep + 1).trim() };
                        }
                        return { value: l, text: l };
                    });
            },
        },
        methods: {
            empty_form() {
                return {
                    name: "",
                    libelle: "",
                    description: "",
                    type_key: null,
                    html_type: "",
                    textarea_rows: 5,
                    list_multiple: false,
                    table: "",
                    type_doc: "",
                    default_value: "",
                    required: true,
                    list_options_raw: "",
                    show_texte_advanced: false,
                    datalist_raw: "",
                    db_value: "",
                    db_libelle: "",
                    db_table: "",
                    db_where: "",
                    db_end: "",
                    targets: [],
                };
            },
            get_key(param, idx) {
                return (param.name || "") + "_" + idx;
            },

            // ── Helpers pour l'affichage ─────────────────────────────────────

            is_v2(param) {
                return Array.isArray(param.targets);
            },
            has_modified_value(param) {
                if (!this.is_v2(param)) return false;
                return param.value !== undefined && param.value !== param.default_value;
            },
            type_label(param) {
                if (!param.type) return "";
                if (typeof param.type === "string") return param.type;
                if (param.type.type === "texte") return param.type.html_type || "texte";
                return param.type.type || "";
            },

            // ── Conversion type_key ↔ objet type ────────────────────────────

            type_to_key(type_obj) {
                if (!type_obj) return "texte";
                switch (type_obj.type) {
                    case "texte":
                        if (type_obj.html_type === "date")   return "date";
                        if (type_obj.html_type === "number") return "nombre";
                        return "texte";
                    case "textarea":           return "textarea";
                    case "textarea_plus":      return "textarea_plus";
                    case "list":               return "liste";
                    case "checkbox":           return "checkbox";
                    case "radio":              return "radio";
                    case "autocomplete":       return "autocomplete";
                    case "autocomplete_list":  return "autocomplete_list";
                    case "draggable_list":     return "draggable_list";
                    case "csv":                return "csv";
                    case "object":             return "object";
                    case "database":           return "database";
                    case "database multitables": return "database_multi";
                    case "documents":          return "documents";
                    case "pdf_zones":          return "pdf_zones";
                    case "html_zones":         return "html_zones";
                    case "mapping":            return "mapping";
                    case "docx":               return "docx";
                    case "file_names":         return "file_names";
                    case "json_schema_editor": return "json_schema_editor";
                    case "hidden":             return "hidden";
                    case "null":               return "null";                    case "booleen":          return "booleen";                    default:                   return "texte";
                }
            },
            key_to_type(key) {
                var opts_vt = (this.form.list_options_raw || "").split("\n")
                    .map(l => l.trim()).filter(l => l)
                    .map(l => { var s = l.indexOf(":"); return s > 0 ? { value: l.slice(0,s).trim(), text: l.slice(s+1).trim() } : { value: l, text: l }; });
                var opts_text = (this.form.list_options_raw || "").split("\n")
                    .map(l => l.trim()).filter(l => l).map(l => ({ text: l }));
                var opts_str = (this.form.list_options_raw || "").split("\n")
                    .map(l => l.trim()).filter(l => l);
                switch (key) {
                    case "texte": {
                        var t_obj = { type: "texte", html_type: this.form.html_type || "text" };
                        var dl = (this.form.datalist_raw || "").split("\n").map(l => l.trim()).filter(l => l);
                        if (dl.length > 0) t_obj.datalist = dl;
                        if (this.form.db_table) {
                            t_obj.db = {
                                value: this.form.db_value || "",
                                libelle: this.form.db_libelle || "",
                                table: this.form.db_table,
                                where: this.form.db_where || "",
                                end: this.form.db_end || "",
                            };
                        }
                        return t_obj;
                    }
                    case "nombre":           return { type: "texte", html_type: "number" };
                    case "booleen":          return { type: "booleen" };
                    case "textarea":         return { type: "textarea", rows: this.form.textarea_rows || 5 };
                    case "textarea_plus":    return { type: "textarea_plus" };
                    case "date":             return { type: "texte", html_type: "date" };
                    case "liste":            return { type: "list", options: opts_vt, multiple: !!this.form.list_multiple };
                    case "checkbox":         return { type: "checkbox", options: opts_vt };
                    case "radio":            return { type: "radio", options: opts_vt };
                    case "autocomplete":     return { type: "autocomplete", options: opts_text };
                    case "autocomplete_list": return { type: "autocomplete_list", datalist: opts_str };
                    case "draggable_list":   return { type: "draggable_list", options: opts_vt };
                    case "csv":              return { type: "csv" };
                    case "object":           return { type: "object" };
                    case "database":         return { type: "database", table: this.form.table || "" };
                    case "database_multi":   return { type: "database multitables" };
                    case "documents":        return { type: "documents" };
                    case "pdf_zones":        return { type: "pdf_zones", type_doc: this.form.type_doc || "" };
                    case "html_zones":       return { type: "html_zones" };
                    case "mapping":          return { type: "mapping" };
                    case "docx":             return { type: "docx", type_doc: this.form.type_doc || "" };
                    case "file_names":       return { type: "file_names" };
                    case "json_schema_editor": return { type: "json_schema_editor" };
                    case "hidden":           return { type: "hidden" };
                    case "null":             return { type: "null" };
                    default:                 return { type: "texte", html_type: "text" };
                }
            },
            on_type_change() {
                this.form.default_value = this.form.type_key === 'booleen' ? false : "";
            },
            effective_type_key() {
                // null = auto, traité comme texte au moment de la sauvegarde
                return this.form.type_key || "texte";
            },

            // ── Helpers pour les étapes ──────────────────────────────────────

            get_step_action(step_uid) {
                var s = this.steps.find(s => s.uid === step_uid);
                return s ? (s.action || "") : "";
            },
            get_step_known_fields(step_uid) {
                var action = this.get_step_action(step_uid);
                return getKnownFields(action);
            },
            get_known_field_options(step_uid) {
                var fields = this.get_step_known_fields(step_uid);
                if (!fields || fields.length === 0) return [];
                var opts = fields.map(f => ({
                    value: f.jsonpath,
                    text: f.label,
                }));
                opts.push({ value: "__custom__", text: "— JSONPath libre —" });
                return opts;
            },
            is_script_action(step_uid) {
                var action = this.get_step_action(step_uid);
                return isScriptAction(action);
            },
            get_child_param_options(step_uid) {
                var s = this.steps.find(s => s.uid === step_uid);
                if (!s || !Array.isArray(s.child_params) || s.child_params.length === 0) return [];
                var opts = s.child_params.map(function(p) {
                    return { value: p.name, text: (p.libelle || p.name) + ' (' + p.name + ')' };
                });
                opts.unshift({ value: '', text: '— Sélectionner —' });
                return opts;
            },
            on_child_param_select(step_uid, param_name) {
                if (!param_name) return;
                var s = this.steps.find(s => s.uid === step_uid);
                if (!s || !Array.isArray(s.child_params)) return;
                var cp = s.child_params.find(p => p.name === param_name);
                if (!cp) return;
                // Auto-fill libelle si vide
                if (!this.form.libelle) this.form.libelle = cp.libelle || cp.name;
                // Auto-fill name si vide
                if (!this.form.name) this.form.name = cp.name;
                // Auto-fill type si null (auto)
                if (this.form.type_key === null) {
                    this.form.type_key = this.type_to_key(cp.type);
                    if (cp.type && cp.type.html_type) this.form.html_type = cp.type.html_type;
                    this.form.default_value = cp.default_value !== undefined ? cp.default_value : "";
                }
            },

            // ── Gestion des cibles ───────────────────────────────────────────

            on_target_step_change(tidx) {
                this.$set(this.form.targets[tidx], "_field_key", null);
                this.$set(this.form.targets[tidx], "jsonpath", "");
                this.$set(this.form.targets[tidx], "param_name", "");
            },
            on_field_select(tidx, val) {
                if (val && val !== "__custom__") {
                    this.$set(this.form.targets[tidx], "jsonpath", val);
                    // Auto-fill type si non défini
                    if (this.form.type_key === null) {
                        var step_uid = this.form.targets[tidx].step_uid;
                        var action = this.get_step_action(step_uid);
                        var known = getKnownFields(action);
                        var field = known.find(function(f) { return f.jsonpath === val; });
                        if (field) {
                            var inferred_type = field.type || "texte";
                            this.form.type_key = inferred_type;
                            this.form.default_value = inferred_type === 'booleen' ? false : "";
                            // Auto-fill libelle depuis le label du champ
                            if (field.label && !this.form.libelle)
                                this.form.libelle = field.label;
                        }
                    }
                } else {
                    this.$set(this.form.targets[tidx], "jsonpath", "");
                }
            },
            add_target() {
                this.form.targets.push({
                    step_uid: null,
                    jsonpath: "",
                    param_name: "",
                    _field_key: null,
                });
            },
            remove_target(tidx) {
                this.form.targets.splice(tidx, 1);
            },

            // ── Modal ────────────────────────────────────────────────────────

            open_create() {
                this.editing_idx = -1;
                this.form = this.empty_form();
                this.name_error = "";
                this.show_modal = true;
            },
            /** Ouvre la modal de création pré-remplie avec l'étape donnée comme cible. */
            open_create_with_step(step_uid) {
                this.editing_idx = -1;
                this.form = this.empty_form();
                this.form.targets = [{
                    step_uid,
                    jsonpath: "",
                    param_name: "",
                    _field_key: null,
                }];
                this.name_error = "";
                this.show_modal = true;
            },
            /** Ouvre la modal d'édition pour un param identifié par son nom. */
            open_edit_by_name(name) {
                var idx = this.params_list.findIndex(p => p.name === name);
                if (idx !== -1) this.open_edit(idx);
            },
            open_edit(idx) {
                var param = this.params_list[idx];
                this.editing_idx = idx;
                this.name_error = "";

                // Reconstituer les champs supplémentaires depuis type
                var html_type = "";
                var textarea_rows = 5;
                var list_multiple = false;
                var table = "";
                var type_doc = "";
                var list_options_raw = "";
                var datalist_raw = "";
                var db_value = "", db_libelle = "", db_table = "", db_where = "", db_end = "";
                var show_texte_advanced = false;
                if (param.type) {
                    var t = param.type;
                    if (t.type === "texte" && t.html_type && t.html_type !== "text") html_type = t.html_type;
                    if (t.type === "textarea" && t.rows) textarea_rows = t.rows;
                    if (t.type === "list") list_multiple = !!t.multiple;
                    if (t.type === "database" && t.table) table = t.table;
                    if ((t.type === "pdf_zones" || t.type === "docx") && t.type_doc) type_doc = t.type_doc;
                    if (t.type === "texte" && Array.isArray(t.datalist) && t.datalist.length > 0) {
                        datalist_raw = t.datalist.join("\n");
                        show_texte_advanced = true;
                    }
                    if (t.type === "texte" && t.db && t.db.table) {
                        db_value = t.db.value || "";
                        db_libelle = t.db.libelle || "";
                        db_table = t.db.table || "";
                        db_where = t.db.where || "";
                        db_end = t.db.end || "";
                        show_texte_advanced = true;
                    }
                    var _opts_types = ["list", "checkbox", "radio", "draggable_list"];
                    if (_opts_types.includes(t.type) && Array.isArray(t.options)) {
                        list_options_raw = t.options.map(o => o.value === o.text ? o.value : o.value + ":" + o.text).join("\n");
                    } else if (t.type === "autocomplete" && Array.isArray(t.options)) {
                        list_options_raw = t.options.map(o => o.text || "").filter(s => s).join("\n");
                    } else if (t.type === "autocomplete_list" && Array.isArray(t.datalist)) {
                        list_options_raw = t.datalist.join("\n");
                    }
                }

                // Reconstituer les targets avec _field_key pour le sélecteur
                var targets = [];
                if (Array.isArray(param.targets)) {
                    targets = param.targets.map(t => {
                        var action = this.get_step_action(t.step_uid);
                        var known  = getKnownFields(action);
                        var _field_key = null;
                        if (t.jsonpath && known.length > 0) {
                            var match = known.find(f => f.jsonpath === t.jsonpath);
                            _field_key = match ? t.jsonpath : "__custom__";
                        }
                        return Object.assign({}, t, { _field_key });
                    });
                } else if (param.step_uid) {
                    // Param v1 — convertir en target v2 à l'édition
                    targets = [{
                        step_uid: param.step_uid,
                        jsonpath: param.jsonpath || "",
                        param_name: "",
                        _field_key: "__custom__",
                    }];
                }

                this.form = {
                    name: param.name || "",
                    libelle: param.libelle || "",
                    description: param.description || "",
                    type_key: this.type_to_key(param.type),
                    html_type,
                    textarea_rows,
                    list_multiple,
                    table,
                    type_doc,
                    default_value: param.default_value !== undefined
                        ? param.default_value
                        : (param.default !== undefined ? param.default : ""),
                    required: !!param.required,
                    list_options_raw,
                    show_texte_advanced,
                    datalist_raw,
                    db_value,
                    db_libelle,
                    db_table,
                    db_where,
                    db_end,
                    targets,
                };

                this.show_modal = true;
            },
            on_modal_ok(event) {
                if (!this.validate_name()) {
                    event.preventDefault();
                    return;
                }
                this.save_param();
            },
            reset_form() {
                this.form = this.empty_form();
                this.name_error = "";
            },

            // ── Validation ───────────────────────────────────────────────────

            validate_name(val) {
                val = (val !== undefined ? val : this.form.name || "").trim();
                if (!val) {
                    this.name_error = "Le nom est obligatoire.";
                    return false;
                }
                if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(val)) {
                    this.name_error = "Doit commencer par une lettre ou _, puis lettres, chiffres et _.";
                    return false;
                }
                var dup = this.params_list.findIndex((p, i) => p.name === val && i !== this.editing_idx);
                if (dup !== -1) {
                    this.name_error = "Ce nom est déjà utilisé par un autre paramètre.";
                    return false;
                }
                this.name_error = "";
                return true;
            },

            // ── Sauvegarde ───────────────────────────────────────────────────

            save_param() {
                var type_obj = this.key_to_type(this.effective_type_key());

                // Nettoyer les targets : supprimer champ interne _field_key, filtrer les entrées incomplètes
                var targets = this.form.targets
                    .filter(t => t.step_uid)
                    .map(t => {
                        var action = this.get_step_action(t.step_uid);
                        if (action === "projet") {
                            return { step_uid: t.step_uid, param_name: t.param_name || "" };
                        }
                        return { step_uid: t.step_uid, jsonpath: t.jsonpath || "" };
                    })
                    .filter(t => t.jsonpath || t.param_name);

                var param = {
                    name: this.form.name.trim(),
                    libelle: this.form.libelle.trim() || this.form.name.trim(),
                    description: this.form.description.trim(),
                    type: type_obj,
                    default_value: this.form.default_value,
                    required: this.form.required,
                    targets,
                };

                // Conserver la valeur courante (test / script) si on édite
                if (this.editing_idx !== -1 && this.params_list[this.editing_idx]) {
                    var existing = this.params_list[this.editing_idx];
                    if (existing.value !== undefined) {
                        param.value = existing.value;
                    }
                }

                if (this.editing_idx === -1) {
                    this.params_list.push(param);
                } else {
                    this.$set(this.params_list, this.editing_idx, param);
                }

                this.emit_change();
                this.show_modal = false;
            },

            // ── Actions sur la liste ─────────────────────────────────────────

            confirm_delete(idx) {
                var label = this.params_list[idx].libelle || this.params_list[idx].name;
                if (confirm('Supprimer le paramètre "' + label + '" ?')) {
                    this.params_list.splice(idx, 1);
                    this.emit_change();
                }
            },
            reset_value(idx) {
                this.$set(this.params_list[idx], "value", this.params_list[idx].default_value);
                this.emit_change();
            },
            emit_change() {
                this.$emit("input", clone(this.params_list));
            },
        },
    };
</script>

<style scoped>
.tp-drag { opacity: 0.5; }
.tp-drag:hover { opacity: 1; }
</style>
